import $script from 'scriptjs';
import { googleMapUrlWithKey } from '../constants';

// Reverse geocoding through the Maps JavaScript API (google.maps.Geocoder).
// The portal's browser key has a website restriction, and Google's Geocoding
// web service rejects keys with one, so the browser must not call it.

/** The parts of a Geocoder result that the portal reads. */
export interface IGeocoderResult {
  formatted_address: string;
  types: string[];
}

/** Shaped like the web service's JSON body, so callers read it as before. */
export interface IReverseGeocodeResponse {
  status: string;
  results: IGeocoderResult[];
}

// Google calls window.gm_authFailure when it refuses the key, for example on a
// host that is missing from the key's website restriction. The Geocoder then
// never calls back, so waiting and later requests end with REQUEST_DENIED,
// which is what the web service answered for a refused key.
let keyRefused = false;
const waiting = new Set<() => void>();
const previousAuthFailure = (window as any).gm_authFailure;
(window as any).gm_authFailure = () => {
  keyRefused = true;
  waiting.forEach((refuse) => refuse());
  waiting.clear();
  if (typeof previousAuthFailure === 'function') {
    previousAuthFailure();
  }
};

// Same loader and URL as RubbleMap (withScriptjs of react-google-maps).
// scriptjs loads each URL once, so the API loads once, whoever asks first.
const loadMapsApi = () =>
  new Promise<any>((resolve, reject) => {
    $script(googleMapUrlWithKey, () => {
      const maps = (window as any).google?.maps;
      if (maps?.Geocoder) {
        resolve(maps);
      } else {
        reject(new Error('Google Maps JavaScript API did not load'));
      }
    });
  });

export const reverseGeocode = (
  latitude: number,
  longitude: number
): Promise<IReverseGeocodeResponse> =>
  loadMapsApi().then(
    (maps) =>
      new Promise<IReverseGeocodeResponse>((resolve) => {
        const refuse = () => resolve({ status: 'REQUEST_DENIED', results: [] });
        if (keyRefused) {
          refuse();
          return;
        }
        waiting.add(refuse);
        const pending = new maps.Geocoder().geocode(
          { location: { lat: latitude, lng: longitude } },
          (results: IGeocoderResult[] | null, status: string) => {
            waiting.delete(refuse);
            resolve({ status, results: results || [] });
          }
        );
        // geocode() also returns a promise, which rejects when the request
        // fails. The callback has the status already, so drop the rejection.
        if (pending && typeof pending.catch === 'function') {
          pending.catch(() => undefined);
        }
      })
  );
